/**
 * space/index.js — the player's personal space (GDD 9).
 *
 * A room, its shelves, its fish tank, its decorations and its guestbook. Built
 * once at a far corner of the ground plane and hidden; stepping through the hub
 * portal hides the midway, switches the lighting indoors and blinks you across.
 * Both stay loaded, so coming and going costs a teleport rather than a rebuild.
 *
 * Three instances of the same machinery exist:
 *   - yours, editable, driven by `save.data`
 *   - Gustav's, read-only and deterministic, so a solo player can still see
 *     what visiting somebody looks like
 *   - a visitor slot the networking agent fills via `renderVisit(serialised)`
 *
 * ── Interface contract (stable; extend additively) ──
 *   init(refs) / update(dt)                 wired from main.js
 *   enter() / exit() / isInside()           the stub's original surface
 *   shelves.place|list|capacity             "
 *   tank.addFish|list|capacity|nameFish     "
 *   visit(playerId)                         "
 *   serialize(owner) → SpaceModel           for the networking agent
 *   renderVisit(model) / endVisit()         "
 *   guestbook.add(reaction, fromName)       "
 *   testApi                                 surfaced as `window.__cc.space`
 *
 * @module space
 */

import * as THREE from 'three';
import { SPACE, HUB, EVENTS, SHELF_THEMES } from '../constants.js';
import { bus } from '../core/events.js';
import { save, economy } from '../core/save.js';
import { teleport } from '../core/teleport.js';
import { input } from '../core/input.js';
import { logCall } from '../core/testmode.js';
import { pointer, menuHost } from './pointer.js';
import { setIndoorLighting, roomNodeLayout, buildNeighbourDoor } from './space-world.js';
import { tankTier } from './fish-tank.js';
import { NamingUI } from './naming-ui.js';
import { SpaceInstance, playerModel, npcModel, blankModel } from './model.js';
import { makeTestApi } from './test-api.js';
import * as shop from './shop.js';

/** Live subsystem references, filled by `init()` from main.js. */
const ctx = { ready: false };

/** @type {SpaceInstance|null} */ let own = null;
/** @type {SpaceInstance|null} */ let npc = null;
/** @type {SpaceInstance|null} */ let guest = null;
/** @type {SpaceInstance|null} Which space the player is standing in. */
let current = null;
/** @type {NamingUI|null} */ let naming = null;
let promptKey = '';

const NPC_ID = 'npc:gustav';

// ─────────────────────────────────────────────────────────────────────────────
// Boot
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {{ engine: object, scene: THREE.Scene, camera: THREE.Camera,
 *           avatar: object, hub: object }} refs
 */
export function init(refs) {
  if (ctx.ready) return;
  Object.assign(ctx, refs);
  ctx.ready = true;

  pointer.init(ctx.camera, ctx.engine?.renderer?.domElement);
  naming = new NamingUI({
    onName: nameFish,
    defaultName: defaultFishName,
  }).init(ctx.camera);

  // Only the player's own room is built at boot. Gustav's and the visitor slot
  // are a room, a tank and a shelf wall each, and most sessions never open
  // either — building them eagerly would put that on every player's load time.
  own = new SpaceInstance({
    name: 'own', origin: SPACE.origin, title: 'YOUR SPACE', hooks: ownerHooks(),
  }).build(ctx.scene);
  npc = new SpaceInstance({
    name: 'npc', origin: SPACE.npcOrigin, title: "GUSTAV'S SPACE", readOnly: true,
  });
  guest = new SpaceInstance({
    name: 'guest', origin: SPACE.visitorOrigin, title: 'VISITING', readOnly: true,
  });

  shop.attach({ own, refresh });
  own.apply(playerModel());
  registerNodes();

  bus.on(EVENTS.DIRECT_PRIZE, onDirectPrize);
  bus.on(EVENTS.STALL_ROUND_END, announcePendingNames);
  bus.on(EVENTS.SAVE_LOAD, () => { if (own) own.apply(playerModel()); });
}

/** Callbacks the owner's instance hands to its subsystems. */
function ownerHooks() {
  return {
    stars: () => economy.stars(),
    onBuyExpansion: shop.buyShelfExpansion,
    onOpenThemes: shop.openShelfThemes,
    onUpgradeTank: shop.upgradeTank,
    onRenameFish: (fishId) => naming.open(fishId),
    onPlaceDecor: shop.placeDecor,
    onRemoveDecor: shop.removeDecor,
    onRoomTheme: shop.setRoomTheme,
    onMusic: shop.setMusic,
    onSign: (reactionId) => addReaction(reactionId, 'You'),
  };
}

/**
 * Build a lazily-created room the first time somebody opens its door.
 * @param {SpaceInstance} instance
 * @param {() => import('./model.js').SpaceModel} model
 * @returns {SpaceInstance}
 */
function ensureBuilt(instance, model) {
  if (!instance.room._built) {
    instance.build(ctx.scene);
    instance.apply(model());
  }
  return instance;
}

/** Register the room's teleport nodes and the neighbour's door in the hub. */
function registerNodes() {
  for (const n of roomNodeLayout()) {
    teleport.addNode({
      id: n.id, position: own.room.toWorld(n.x, 0, n.z), yaw: n.yaw,
      label: n.id, group: 'space',
    });
  }
  teleport.addNode({
    id: 'gustav-space',
    position: new THREE.Vector3(HUB.personalSpacePortal.x + 3.4, 0, HUB.personalSpacePortal.z + 2.2),
    yaw: 0, label: "Gustav's Space", group: 'landmark',
  });
  buildNeighbourDoor(ctx.hub?.root || ctx.scene, HUB.personalSpacePortal.x + 3.4, HUB.personalSpacePortal.z);
}

// ─────────────────────────────────────────────────────────────────────────────
// Entering and leaving
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Step into your own space.
 * @returns {Promise<boolean>} False when a stall round is in the way.
 */
export async function enter() {
  logCall('space', 'enter', []);
  return goTo(own);
}

/**
 * @param {string} playerId `npc:gustav`, or the owner of a rendered visit.
 * @returns {Promise<boolean>}
 */
export async function visit(playerId) {
  logCall('space', 'visit', [playerId]);
  if (playerId === NPC_ID || playerId === 'gustav') return goTo(ensureBuilt(npc, npcModel));
  if (guest?.model && guest.model.owner === playerId) return goTo(guest);
  return false;
}

/** @returns {Promise<boolean>} */
export async function exit() {
  logCall('space', 'exit', []);
  if (!current) return true;
  const room = current.room;
  current = null;
  menuHost.close();
  pointer.enabled = false;
  setPrompt('', '');

  await blink(() => {
    room.setVisible(false);
    if (ctx.hub?.root) ctx.hub.root.visible = true;
    setIndoorLighting(ctx.scene, false);
    ctx.avatar.setPosition(
      new THREE.Vector3(HUB.personalSpacePortal.x, 0, HUB.personalSpacePortal.z + 2.6),
      Math.PI,
    );
  });
  bus.emit(EVENTS.AUDIO_PLAY, { name: 'space-exit' });
  return true;
}

/** @returns {boolean} */
export function isInside() {
  return current !== null;
}

/** @private */
async function goTo(instance) {
  if (!ctx.ready || !instance) return false;
  if (current === instance) return true;
  if (ctx.avatar.getMode() === 'first') {
    toast('Leave the stall first', 'bad');
    return false;
  }
  const leaving = current;
  current = instance;

  await blink(() => {
    if (leaving) leaving.room.setVisible(false);
    if (ctx.hub?.root) ctx.hub.root.visible = false;
    instance.room.setVisible(true);
    setIndoorLighting(ctx.scene, true);
    // Far enough from the exit portal that its prompt is not the first thing
    // you see, and facing the tank wall so the camera frames the room.
    ctx.avatar.setPosition(instance.room.toWorld(0.4, 0, -1.4), 0);
  });

  pointer.enabled = true;
  bus.emit(EVENTS.AUDIO_PLAY, {
    name: 'space-music',
    track: instance === own ? save.data.space.music : 'gentle-waltz',
  });
  if (instance === own && naming.pending().length) naming.open();
  return true;
}

/** Run a callback at the darkest point of a comfort blink (GDD 12.3). */
function blink(atBlack) {
  return new Promise((resolve) => teleport.blink(atBlack, resolve));
}

// ─────────────────────────────────────────────────────────────────────────────
// Frame
// ─────────────────────────────────────────────────────────────────────────────

/** @param {number} dt */
export function update(dt) {
  if (!ctx.ready) return;
  if (!current) {
    updateHubPrompt();
    return;
  }
  current.update(dt, ctx.camera);
  current.room.clampToRoom(ctx.avatar.position);
  pointer.update();
  updateExitPrompt();
}

/** Proximity prompts for the two doors at the end of the midway. */
function updateHubPrompt() {
  const p = ctx.avatar.getPosition();
  const nearOwn = distanceTo(p, HUB.personalSpacePortal.x, HUB.personalSpacePortal.z) < SPACE.portalRadius;
  const nearNpc = !nearOwn
    && distanceTo(p, HUB.personalSpacePortal.x + 3.4, HUB.personalSpacePortal.z) < SPACE.portalRadius;

  setPrompt(
    nearOwn ? 'own' : nearNpc ? 'npc' : '',
    nearOwn ? 'Press <kbd>F</kbd> to enter your space'
      : "Press <kbd>F</kbd> to visit Gustav's space",
  );

  if (!input.wasPressed('activate')) return;
  if (nearOwn) enter();
  else if (nearNpc) visit(NPC_ID);
}

function updateExitPrompt() {
  const p = ctx.avatar.getPosition();
  const portal = current.room.exitPosition();
  const near = distanceTo(p, portal.x, portal.z) < 1.9;
  setPrompt(near ? 'exit' : '', 'Press <kbd>F</kbd> to head back to the midway');
  if (near && input.wasPressed('activate')) exit();
}

function setPrompt(key, text) {
  if (key === promptKey) return;
  promptKey = key;
  bus.emit(EVENTS.UI_PROMPT, { text, show: !!key });
}

function distanceTo(p, x, z) {
  return Math.hypot(p.x - x, p.z - z);
}

function toast(text, kind = 'good', duration = 2600) {
  bus.emit(EVENTS.UI_TOAST, { text, kind, duration });
}

/** Re-read the save and re-furnish the owner's room. */
function refresh() {
  save.markDirty();
  own.apply(playerModel());
}

// ─────────────────────────────────────────────────────────────────────────────
// Fish naming (GDD 9.3)
// ─────────────────────────────────────────────────────────────────────────────

function onDirectPrize(prize) {
  if (!prize) return;
  if (prize.kind === 'fish') naming.enqueue(prize.id);
  // A prize won at any stall has to appear here, so re-read rather than patch.
  if (own) own.apply(playerModel());
}

function announcePendingNames() {
  const n = naming.pending().length;
  if (n) toast(`Name your new fish back at your space (${n} waiting)`, 'prize', 3600);
}

/**
 * The name a fish falls back to when the prompt is dismissed (GDD 9.3).
 * @param {string} fishId
 * @returns {string}
 */
export function defaultFishName(fishId) {
  const i = save.data.prizes.fish.findIndex((f) => f.id === fishId);
  return `Fish #${(i < 0 ? save.data.prizes.fish.length : i) + 1}`;
}

/**
 * @param {string} fishId @param {string} name
 * @returns {boolean}
 */
export function nameFish(fishId, name) {
  const record = save.data.prizes.fish.find((f) => f.id === fishId);
  if (!record) return false;
  record.name = String(name).slice(0, 24);
  refresh();
  toast(`Say hello to ${record.name}`, 'prize');
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Guestbook (GDD 9.4)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Leave a reaction in your own guestbook. The networking agent calls this for
 * remote visitors; self-visits are allowed so the feature is demonstrable solo.
 * @param {string} reactionId `honk` | `applause` | `wow`
 * @param {string} [fromName]
 * @returns {object|null}
 */
export function addReaction(reactionId, fromName = 'A goose') {
  const entry = own?.guestbook.add(reactionId, fromName, save.today());
  if (!entry) return null;
  save.data.space.guestbook = own.guestbook.list();
  save.markDirty();
  bus.emit(EVENTS.AUDIO_PLAY, { name: `guest-${entry.reaction}` });
  return entry;
}

export const guestbook = {
  /** @param {string} reactionId @param {string} fromName @returns {object|null} */
  add: (reactionId, fromName) => addReaction(reactionId, fromName),
  /** @returns {Array<object>} Oldest first, capped at twenty. */
  list: () => (own ? own.guestbook.list() : save.data.space.guestbook.slice()),
};

// ─────────────────────────────────────────────────────────────────────────────
// The original stub surface, now real
// ─────────────────────────────────────────────────────────────────────────────

export const shelves = {
  /** @param {string} prizeId @param {number} slot @returns {boolean} */
  place(prizeId, slot) {
    logCall('space', 'shelves.place', [prizeId, slot]);
    if (!own || !own.shelves.place(prizeId, slot)) return false;
    save.data.space.arrangement = own.shelves.arrangement();
    save.markDirty();
    return true;
  },
  /**
   * Record a plush won elsewhere. The same `economy.awardDirectPrize` call a
   * stall makes at GDD 7.4 payout, exposed so the prize desk (and a test) can
   * stock a shelf without playing a round.
   * @param {'plush-small'|'plush-large'} kind
   * @param {string} [stallId]
   * @returns {object} The stored record.
   */
  award(kind = 'plush-small', stallId = 'tin-can-alley') {
    logCall('space', 'shelves.award', [kind, stallId]);
    return economy.awardDirectPrize({ kind, stallId });
  },
  /** Pick a plush up; the next slot selected becomes its home. */
  lift: (prizeId) => (own ? own.shelves.lift(prizeId) : false),
  /** @param {number} slot @returns {boolean} */
  placeHeld(slot) {
    if (!own || !own.shelves.placeHeld(slot)) return false;
    save.data.space.arrangement = own.shelves.arrangement();
    save.markDirty();
    return true;
  },
  /** @returns {Array<object>} */
  list: () => (own ? own.shelves.list() : save.data.prizes.plush.slice()),
  /** @returns {number} */
  capacity: () => save.data.space.shelfSlots,
  /** @returns {Record<string, number>} prizeId → slot */
  arrangement: () => (own ? own.shelves.arrangement() : { ...save.data.space.arrangement }),
  buyExpansion: shop.buyShelfExpansion,
  setTheme: shop.setShelfTheme,
  themes: () => SHELF_THEMES.map((t) => ({ ...t, owned: shop.owns(t.id) })),
};

export const tank = {
  /**
   * Record a fish won elsewhere. Stalls normally reach this through
   * `economy.awardDirectPrize` and the DIRECT_PRIZE event; this is the direct
   * path, used by the prize desk and by tests.
   * @param {{ variant?: string, name?: string, stallId?: string }} fish
   * @returns {object|null} The stored record.
   */
  addFish(fish) {
    logCall('space', 'tank.addFish', [fish]);
    return economy.awardDirectPrize({
      kind: 'fish',
      variant: fish.variant || 'orange',
      stallId: fish.stallId || null,
      name: fish.name || null,
    });
  },
  /** @returns {Array<object>} Displayed fish, with live positions. */
  list: () => (own ? own.tank.list() : save.data.prizes.fish.slice()),
  /** @returns {number} */
  capacity: () => save.data.space.tankCapacity,
  /** @returns {object} */
  tier: () => tankTier(save.data.space.tankCapacity),
  /** @param {string} fishId @param {string} name @returns {boolean} */
  nameFish(fishId, name) {
    logCall('space', 'tank.nameFish', [fishId, name]);
    return nameFish(fishId, name);
  },
  /** @returns {Array<object>} Fish the tank is currently too small to show. */
  waitlist: () => (own
    ? own.tank.waitlist()
    : save.data.prizes.fish.slice(save.data.space.tankCapacity)),
  /** @param {string} fishId @returns {object|null} */
  info: (fishId) => (own ? own.tank.info(fishId) : null),
  upgrade: shop.upgradeTank,
};

// ─────────────────────────────────────────────────────────────────────────────
// Sharing — what the networking agent reads and writes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A portable snapshot: everything `renderVisit` needs, deep-copied so a visitor
 * cannot reach back into the original.
 * @param {string} [owner]
 * @returns {import('./model.js').SpaceModel}
 */
export function serialize(owner = 'You') {
  return JSON.parse(JSON.stringify({ ...playerModel(), owner }));
}

/**
 * Build a read-only copy of somebody else's space and step into it.
 * @param {object} model A `serialize()` payload.
 * @returns {Promise<boolean>}
 */
export async function renderVisit(model) {
  logCall('space', 'renderVisit', [model && (model.owner || model.ownerName)]);
  if (!model || !guest) return false;
  ensureBuilt(guest, blankModel);

  // The networking agent labels the sender `ownerName` and its fallback
  // snapshot omits several keys outright, so merge over a blank and drop any
  // null a sparse payload carried in.
  const merged = { ...blankModel() };
  for (const [k, v] of Object.entries(model)) if (v !== null && v !== undefined) merged[k] = v;
  merged.owner = model.owner || model.ownerName || 'A goose';

  guest.apply(merged);
  guest.room.setTitle(`${merged.owner.toUpperCase()}'S SPACE`);
  return goTo(guest);
}

/** @returns {Promise<boolean>} Leave a visitor space. */
export async function endVisit() {
  if (current !== guest && current !== npc) return false;
  return exit();
}

// ─────────────────────────────────────────────────────────────────────────────
// window.__cc.space — surfaced by core/testmode.js via `wired.space.testApi`
// ─────────────────────────────────────────────────────────────────────────────

export const testApi = makeTestApi({
  enter, exit, isInside, visit, serialize, renderVisit, endVisit, nameFish,
  shelves, tank, guestbook, ctx, NPC_ID,
  naming: () => naming,
  get: { own: () => own, current: () => current },
});

export default {
  init, update, enter, exit, isInside, shelves, tank, visit,
  serialize, renderVisit, endVisit, guestbook, testApi,
};
